<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import { whenever } from '@vueuse/core'
import { isEqual } from 'lodash-es'

import type GiftTemplate from '@consumer/types/GiftTemplate'
import type GTextInput from '@consumer/components/GTextInput.vue'
import {
  generateDefaultTagline,
  generateDefaultTaglines,
  getTagline,
  setTagline,
  validateTagline,
} from '@consumer/logic/giftly'
import { customizer } from '@consumer/api'
import { translateErrorToString, unexpectedErrorMessage } from '@shared/validation'
import type MultiplaceGift from '@consumer/types/serializers/MultiplaceGift'
import { typeaheadDebounce } from '@shared/debounce'
import { faArrowRotateLeft } from '@fortawesome/pro-light-svg-icons'
import newSpinnerIcon from '@consumer/assets/icons/new/spinner.svg?svgicon'

withDefaults(defineProps<{
  gift: GiftTemplate
  isActive?: boolean
  multiplaceGift?: MultiplaceGift
  createOrEditMultiplace?: boolean
  fromTools?: boolean
  originalTagline?: string
  taglineError?: string
  createGiftIdea?: boolean
}>(), { taglineError: '',createGiftIdea: false, })

if (!__props.gift.title && __props.multiplaceGift?.title != null) {
  __props.gift.title = __props.multiplaceGift.title
}

const showTitleEditor = computed(() => {
  return __props.multiplaceGift != null || __props.fromTools === true || __props.createOrEditMultiplace === true || __props.createGiftIdea === true
})
const hasValidTitle = computed(() => !showTitleEditor.value || (__props.gift.title ?? '').length > 0)

const emit = defineEmits<{
  stepComplete: []
  validTagline: [isValid: boolean]
  validTitle: [isValid: boolean]
}>()

const tagline = _computed(() => getTagline(__props.gift))
const defaultTagline = _computed(() => generateDefaultTagline(__props.gift))

onMounted(() => {
  if (!tagline.value) setTagline(__props.gift)
  if (showTitleEditor.value) emit('validTitle', hasValidTitle.value)
})

let taglineErrorMessage = _ref(__props.taglineError)
const titleErrorMessage = _ref('')

watch(
  () => __props.taglineError,
  () => {
    taglineErrorMessage.value = __props.taglineError
    if (__props.taglineError.length) {
      setTimeout(() => {
        inputEl.value?.focus()
      }, 100)
    }
  },
)

const inputEl = _ref<InstanceType<typeof GTextInput>>()

function onClear () {
  setTagline(__props.gift, '', true)
  inputEl.value?.focus()
}

let isLoadingRecommendedTaglines = _ref(false)
let isLoadingRecommendedTitles = _ref(false)
let placeIds = _ref<Array<number | undefined>>([])
let recommmededTaglines = _ref<string[]>([])
let recommmededTitles = _ref<string[]>([])

async function fetchGeneratedTitles () {
  isLoadingRecommendedTitles.value = true
  const data = await customizer.recommendedTitles({
    query: { placeIds: placeIds.value },
  })
  isLoadingRecommendedTitles.value = false
  return data
}

onMounted(async () => {
  recommmededTaglines.value = generateDefaultTaglines(__props.gift)
})

whenever(
  () => __props.isActive,
  () => {
    const newPlaceIds = __props.gift.places?.map(place => place.giftlyId) ?? []
    if (!isEqual(newPlaceIds, placeIds.value)) {
      placeIds.value = newPlaceIds
    }
  },
)

watch(
  () => [placeIds.value],
  async () => {
    isLoadingRecommendedTaglines.value = true
    recommmededTaglines.value = generateDefaultTaglines(__props.gift)

    if (placeIds.value.length > 1) {
      customizer.recommendedTaglines({
        query: { placeIds: placeIds.value },
      }).then((taglines) => {
        recommmededTaglines.value = taglines.concat(generateDefaultTaglines(__props.gift))
        isLoadingRecommendedTaglines.value = false
      }).catch(() => {
        isLoadingRecommendedTaglines.value = false
      })

      recommmededTitles.value = await fetchGeneratedTitles()
    }
    else {
      isLoadingRecommendedTaglines.value = false
    }
  },
)

let checkingTagline = _ref(false)

// Validate that it doesn't contain banned words.
const checkTagline = typeaheadDebounce(async (tagline: string) => validateTagline(tagline, __props.gift))

const fromGcdp = _computed(() => __props.gift.purchaseMethod === 'giftcard')

async function onTaglineChange (tagline?: string, validate?: boolean) {
  try {
    setTagline(__props.gift, tagline, validate)
    taglineErrorMessage.value = ''

    if (validate && tagline?.trim()) {
      emit('validTagline', false)

      if (!fromGcdp.value || __props.originalTagline !== tagline) {
        checkingTagline.value = true
        await checkTagline(tagline)
      }
    }

    if (hasValidTitle) {
      emit('stepComplete')
    }
  }
  catch (error) {
    taglineErrorMessage.value = translateErrorToString(error)
    // Treat as valid if a server error occurred.
    if (taglineErrorMessage.value === unexpectedErrorMessage) taglineErrorMessage.value = ''
  }
  finally {
    emit('validTagline', !taglineErrorMessage.value?.trim())
    checkingTagline.value = false
  }
}

async function onTitleChange (title?: string) {
  __props.gift.title = title ?? ''
  if (__props.multiplaceGift) {
    __props.multiplaceGift.title = __props.gift.title
  }

  emit('validTitle', hasValidTitle.value)
  if (hasValidTitle.value) {
    emit('stepComplete')
  }
}
</script>

<template>
  <div class="tagline-tab">
    <GTextInput
      ref="inputEl"
      :modelValue="tagline"
      name="nc_tagline_input"
      placeholder="Your tagline here"
      placeholderAlignment="center"
      maxlength="65"
      data-testid="tagline-input"
      :hasError="Boolean(taglineErrorMessage)"
      @update:modelValue="onTaglineChange($event, true)"
    >
      <template #addonRight>
        <LoadingIndicator v-if="checkingTagline" size="md"/>
        <ClearTextButton
          v-else-if="tagline === defaultTagline"
          name="nc_tagline_clear"
          data-testid="tagline-input-x"
          @click="onClear"
        />
        <button
          v-else
          name="nc_tagline_reset"
          type="button"
          class="text-v2-secondary flex items-center justify-center hover:text-navy"
          data-testid="tagline-input-reset"
          @click="onTaglineChange()"
        >
          <GIcon :icon="faArrowRotateLeft" size="md"/>
        </button>
      </template>
    </GTextInput>
    <GInputError v-if="taglineErrorMessage" :error="taglineErrorMessage"/>

    <template v-if="showTitleEditor">
      <div class="mt-6">
        <h3 class="text-base font-semibold text-v2-tertiary mb-2">Recommended Taglines</h3>

        <div class="flex flex-col text-navy font-semibold pl-2">
          <button
            v-for="recommendedTagline in recommmededTaglines"
            :key="recommendedTagline"
            name="nc_tagline_recommended"
            type="button"
            class="text-left text-v2-secondary
              hover:text-navy hover:underline hover:underline-offset-4 py-2"
            @click="onTaglineChange(recommendedTagline)"
          >
            {{ recommendedTagline }}
          </button>
        </div>
        <div v-if="isLoadingRecommendedTaglines">
          <div class="flex gap-2 italic text-grey-500">
            <GIcon :svg="newSpinnerIcon" size="16px" color="grey-500" class="animate-spin icon-spinner"/>
            Generating recommendations...
          </div>
        </div>
      </div>

      <div class="mt-6">
        <h1 class="text-center text-2xl my-5">Add a short description</h1>

        <GTextInput
          name="nc_title_input"
          placeholder="Exactly what they want. Wherever they chose."
          maxlength="100"
          data-testid="title-input"
          :hasError="Boolean(titleErrorMessage)"
          :modelValue="gift.title"
          @update:modelValue="onTitleChange($event)"
        />
        <GInputError v-if="titleErrorMessage" :error="titleErrorMessage"/>
      </div>

      <div
        v-if="recommmededTitles.length > 0 || isLoadingRecommendedTitles"
        class="mt-6"
      >
        <h3 class="text-base font-semibold text-v2-tertiary mb-2">Recommended Titles</h3>

        <div class="flex flex-col text-navy font-semibold pl-2">
          <button
            v-for="recommendedTitle in recommmededTitles"
            :key="recommendedTitle"
            name="nc_title_recommended"
            type="button"
            class="text-left text-v2-secondary
              hover:text-navy hover:underline hover:underline-offset-4 py-2"
            data-testid="recommended-title"
            @click="onTitleChange(recommendedTitle)"
          >
            {{ recommendedTitle }}
          </button>
        </div>
        <div v-if="isLoadingRecommendedTitles">
          <div class="flex gap-2 italic text-grey-500">
            <GIcon :svg="newSpinnerIcon" size="16px" color="grey-500" class="animate-spin icon-spinner"/>
            Generating recommendations...
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
